import { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useAuth } from './useAuth';
import { MULTI_TENANT_ENABLED } from '../config/features';

type UseAdminReturn = {
  displayName: string | null;
  tenantName: string | null;
  isAdmin: boolean;
  enabledCards: string[] | null;
  loading: boolean;
  isCustomer: boolean;
};

export function useAdmin(): UseAdminReturn {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [enabledCards, setEnabledCards] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);

  useEffect(() => {
    const checkAdminStatus = async () => {
      setLoading(true);
      if (user && user.email) {
        try {
          const db = getFirestore();
          const userDocSnap = await getDoc(doc(db, 'whitelisted_users', user.email));

          if (userDocSnap.exists()) {
            const data = userDocSnap.data();
            setIsAdmin(data['role'] === 'Admin');
            if (data['displayName']) setDisplayName(data['displayName'] as string);

            if (MULTI_TENANT_ENABLED && data['role'] === 'Customer') {
              if (data['tenant_id']) {
                const tenantDocSnap = await getDoc(doc(db, 'tenants', data['tenant_id']));
                if (tenantDocSnap.exists()) {
                  // null means admin (no filter); [] means customer with no features yet enabled
                  setEnabledCards((tenantDocSnap.data()['enabled_cards'] as string[]) ?? []);
                  if (tenantDocSnap.data()['tenant_name']) setTenantName(tenantDocSnap.data()['tenant_name'] as string);
                } else {
                  setEnabledCards([]);
                }
              } else {
                setEnabledCards([]);
              }
            }
          } else {
            setIsAdmin(false);
          }
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    };

    void checkAdminStatus();
  }, [user]);

  const isCustomer = !loading && enabledCards !== null;
  return { isAdmin, enabledCards, loading, isCustomer, displayName, tenantName };
}
